import type { Dispatch } from 'react';
import { useMemo, useCallback } from 'react';
import { useLocalStorage } from '@hotjar/insights-common/hooks/useStorage';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useCurrentSite } from '@hotjar/site';
import { keysToSnakeCase } from '@hotjar/webapp/lib/convert-string';
import {
  COLUMNS_VISIBLE_DEFAULT,
  COLUMNS_OPTIONS_DEFAULT,
} from '../../../../domain/Surveys/Responses/SurveyResponsesColumns';
import type {
  SurveyColumnOptions,
  SurveyResponsesColumnKeys,
} from '../../../../domain/Surveys/Responses/SurveyResponsesColumns';
import { useTranslatedColumnsOptions } from './useTranslatedColumnsOptions';

export const COLUMNS_VISIBLE_STORAGE_KEY = 'surveysColumnsVisible';

type ColumnsVisible = Record<SurveyResponsesColumnKeys, boolean>;
interface UseTableColumnOptions {
  tableColumnsOptions: SurveyColumnOptions;
  updateColumnsOptions: Dispatch<SurveyColumnOptions>;
}

const useVisibleColumnsUpdateTracking = () => {
  const track = useTrack();
  const { data: site } = useCurrentSite();

  return useCallback(
    (previousColumns: ColumnsVisible, newColumns: ColumnsVisible) => {
      if (!site) return;
      const columnKeys = Object.keys(COLUMNS_OPTIONS_DEFAULT) as SurveyResponsesColumnKeys[];
      const previouslyVisibleColumns = columnKeys.filter((key) => previousColumns[key]);
      const newVisibleColumns = columnKeys.filter((key) => newColumns[key]);
      const columnsShown = newVisibleColumns.filter(
        (key) => !previouslyVisibleColumns.includes(key),
      );
      const columnsHidden = previouslyVisibleColumns.filter(
        (key) => !newVisibleColumns.includes(key),
      );

      track.click(
        'Survey Table Actions',
        'Survey Responses Table View Updated',
        track.getGroupDataForSite(site),
        keysToSnakeCase({
          oldSurveyTableColumns: previouslyVisibleColumns.join(', '),
          newSurveyTableColumns: newVisibleColumns.join(', '),
          columnsSwitchedOn: columnsShown.join(', '),
          columnsSwitchedOff: columnsHidden.join(', '),
        }),
      );
    },
    [site, track],
  );
};

export const useTableColumnsOptions = (hasAnyStatements: boolean): UseTableColumnOptions => {
  const trackColumnsUpdate = useVisibleColumnsUpdateTracking();
  const defaultColumnsOptions = useTranslatedColumnsOptions();

  const [columnsVisible, setColumnsVisible] = useLocalStorage(
    COLUMNS_VISIBLE_STORAGE_KEY,
    COLUMNS_VISIBLE_DEFAULT,
  );

  const tableColumnsOptions = useMemo(() => {
    return (
      Object.keys(defaultColumnsOptions) as SurveyResponsesColumnKeys[]
    ).reduce<SurveyColumnOptions>(
      (newColumnOptions: SurveyColumnOptions, column: SurveyResponsesColumnKeys) => {
        const newColumnOption = {
          ...newColumnOptions,
          [column]: { ...defaultColumnsOptions[column], show: columnsVisible[column] },
        };
        if (column === 'statements' && !hasAnyStatements) {
          newColumnOption[column].showInPanel = false;
        }
        return newColumnOption;
      },
      {} as SurveyColumnOptions,
    );
  }, [columnsVisible, hasAnyStatements, defaultColumnsOptions]);

  const updateColumnsOptions = useCallback(
    (newColumnOptions: SurveyColumnOptions) => {
      const newColumnsVisible = (
        Object.keys(COLUMNS_OPTIONS_DEFAULT) as SurveyResponsesColumnKeys[]
      ).reduce((previousColumnsVisible, columnKey) => {
        return {
          ...previousColumnsVisible,
          [columnKey]: newColumnOptions[columnKey]?.show ?? defaultColumnsOptions[columnKey].show,
        };
      }, {} as ColumnsVisible);
      trackColumnsUpdate(columnsVisible, newColumnsVisible);
      setColumnsVisible(newColumnsVisible);
    },
    [setColumnsVisible, columnsVisible, trackColumnsUpdate, defaultColumnsOptions],
  );

  return { tableColumnsOptions, updateColumnsOptions };
};
